import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useLazyQuery } from '@apollo/client';
import { Dialog, DialogActions, DialogContent, Typography } from '@guildeducationinc/recess';
import { PAYMENT_REQUEST_CLAIMS_DOWNLOAD } from '@src/graphql/queries/paymentRequest';
import { ClaimsTab } from '@src/hooks/usePaymentRequestClaims';
import { PaymentRequestStatus } from '@src/types';
import { saveAs } from 'file-saver';
import { DatePicker, formatDate, LoaderCircleIcon, Messages, MessageType, MuiMultiSelect, TrackableButton, useLocale, } from 'pe-core-frontend';
import { useState } from 'react';
export const ClaimsDownload = () => {
    const { t } = useLocale();
    const [showDownloadReport, setShowDownloadReport] = useState(false);
    const [downloadReport] = useLazyQuery(PAYMENT_REQUEST_CLAIMS_DOWNLOAD, {
        fetchPolicy: 'no-cache', // Important for file downloads
    });
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    // first and last day of the current month
    const todayDate = new Date();
    const firstDayOfMonth = new Date(todayDate.getFullYear(), todayDate.getMonth(), 1);
    const lastDayOfMonth = new Date(todayDate.getFullYear(), todayDate.getMonth() + 1, 0);
    const defaultStartDateStr = formatDate(firstDayOfMonth);
    const defaultEndDateStr = formatDate(lastDayOfMonth);
    const [startDate, setStartDate] = useState(defaultStartDateStr);
    const [endDate, setEndDate] = useState(defaultEndDateStr);
    const defaultSelectedTypes = [
        ClaimsTab.PENDING,
        ClaimsTab.INFO_REQUESTED,
        ClaimsTab.HOLD,
        ClaimsTab.PROCESSED,
    ];
    const [selectedTypes, setSelectedTypes] = useState(defaultSelectedTypes);
    const claimsTabOptions = [
        { value: ClaimsTab.PENDING, label: t('Pending claims') },
        { value: ClaimsTab.INFO_REQUESTED, label: t('Info requested claims') },
        { value: ClaimsTab.HOLD, label: t('On hold claims') },
        { value: ClaimsTab.PROCESSED, label: t('Processed claims') },
    ];
    const handleTypeChange = (event) => {
        setSelectedTypes(event.selection.map((x) => x.value));
    };
    const handleOpenDownloadClick = () => {
        setLoading(false);
        setShowDownloadReport(true);
    };
    const handleDownloadCancel = () => {
        setLoading(false);
        setShowDownloadReport(false);
        setStartDate(defaultStartDateStr);
        setEndDate(defaultEndDateStr);
        setSelectedTypes(defaultSelectedTypes);
        setMessages([]);
    };
    const validateInputs = () => {
        if (selectedTypes.length === 0) {
            setMessages([{ type: MessageType.WARNING, value: t('Please select at least one status') }]);
            return false;
        }
        if (!startDate || !endDate) {
            setMessages([{ type: MessageType.WARNING, value: t('Please select start and end dates') }]);
            return false;
        }
        if (isNaN(Date.parse(startDate)) || isNaN(Date.parse(endDate))) {
            setMessages([{ type: MessageType.WARNING, value: t('Please enter valid dates') }]);
            return false;
        }
        if (new Date(startDate) > new Date(endDate)) {
            setMessages([
                { type: MessageType.WARNING, value: t('End date must be on or after start date') },
            ]);
            return false;
        }
        return true;
    };
    const handleDownloadReport = async () => {
        setMessages([]);
        if (!validateInputs())
            return;
        const selectedStatuses = [];
        if (selectedTypes.includes(ClaimsTab.PENDING)) {
            selectedStatuses.push(PaymentRequestStatus.DOCUMENT_REVIEW);
        }
        if (selectedTypes.includes(ClaimsTab.INFO_REQUESTED)) {
            selectedStatuses.push(PaymentRequestStatus.INFO_REQUESTED);
        }
        if (selectedTypes.includes(ClaimsTab.HOLD)) {
            selectedStatuses.push(PaymentRequestStatus.ON_HOLD);
        }
        if (selectedTypes.includes(ClaimsTab.PROCESSED)) {
            selectedStatuses.push(PaymentRequestStatus.READY_FOR_PAYMENT, PaymentRequestStatus.CLOSED, PaymentRequestStatus.APPROVED, PaymentRequestStatus.REJECTED, PaymentRequestStatus.EXPIRED);
        }
        try {
            setLoading(true);
            const { data, error } = await downloadReport({
                variables: {
                    spec: {
                        startDate,
                        endDate,
                        statuses: selectedStatuses,
                    },
                },
            });
            const result = data?.paymentRequestClaimsDownload;
            if (error) {
                setMessages([{ type: MessageType.ERROR, value: error.message }]);
                return;
            }
            if (!result) {
                setMessages([{ type: MessageType.ERROR, value: t('No data received') }]);
                return;
            }
            if (result.rowCount === 0) {
                setMessages([
                    {
                        type: MessageType.INFO,
                        value: t('No records found for the selected criteria'),
                    },
                ]);
                return;
            }
            let userMessage = '';
            if (result.rowCount < result.totalRowCount) {
                userMessage = t('Report downloaded successfully. Downloaded {count} records of {total} total records found.', { values: { count: result.rowCount, total: result.totalRowCount } });
            }
            else {
                userMessage = t('Report downloaded successfully. Total records: {count}.', {
                    values: { count: result.rowCount },
                });
            }
            setMessages([
                {
                    type: MessageType.SUCCESS,
                    value: userMessage,
                },
            ]);
            // Create blob from the response
            const blob = new Blob([result.contents || ''], {
                type: 'text/csv;charset=utf-8;',
            });
            // Use file-saver to save the file
            saveAs(blob, 'payment_claims.csv');
        }
        catch (ex) {
            // eslint-disable-next-line no-console
            console.error('Download failed:', ex);
            setMessages([
                { type: MessageType.ERROR, value: t('An error occurred when preparing the download') },
            ]);
        }
        finally {
            setLoading(false);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(TrackableButton, { btnType: 'mui', enText: 'Download Report', tag: 'header', color: 'primary', variant: 'contained', type: 'button', className: 'button button--primary margin--bottom_16', onClick: handleOpenDownloadClick, children: t('Download Report') }), showDownloadReport && (_jsxs(Dialog, { isOpen: showDownloadReport, title: t('Download Claims Report'), onClose: handleDownloadCancel, fullScreen: false, forceDisableFocusTrap: true, children: [_jsxs(DialogContent, { children: [_jsx(Messages, { messages: messages, onClose: () => setMessages([]) }), _jsxs("div", { className: 'flex flex-col gap-4', children: [loading && (_jsxs(Typography, { variant: 'body2', className: 'text-center my-4', children: [t('Processing download...'), " ", _jsx(LoaderCircleIcon, { fontSize: 'small' })] })), _jsx(MuiMultiSelect, { label: t('Include Statuses'), onChange: handleTypeChange, options: claimsTabOptions, value: selectedTypes, disabled: loading }), _jsx(DatePicker, { label: t('Start Date'), value: startDate, onChange: startDate => setStartDate(startDate), disabled: loading }), _jsx(DatePicker, { label: t('End Date'), value: endDate || '', onChange: endDate => setEndDate(endDate), disabled: loading })] })] }), _jsxs(DialogActions, { fullScreen: false, children: [_jsx(TrackableButton, { enText: 'Download', tag: 'ClaimsDownloadReportSubmit', appearance: 'primary', onClick: handleDownloadReport, disabled: loading, children: t('Download') }), _jsx(TrackableButton, { enText: 'Close', tag: 'ClaimsDownloadReportCancel', appearance: 'outline', onClick: handleDownloadCancel, children: t('Close') })] })] }))] }));
};
